import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import { Col, Form, Row } from "react-bootstrap";
import { BsDownload } from "react-icons/bs";
import { useSelector, useDispatch } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { MdHighlightOff } from "react-icons/md";
import utils from "lodash";
import IE from "../../components/Formatters/IE";
import DateTimeInputFormik from "../../components/UI/DateTimeInput/DateTimeInputFormik";
import SpfPaginatedView from "../../components/UI/PaginatedView/SpfPaginatedView";
import SelectInput from "../../components/UI/SelectInput/SelectInput";
import SelectInputCachedFormik from "../../components/UI/SelectInput/SelectInputCachedFormik";
import TextInputFormik from "../../components/UI/TextInput/TextInputFormik";
import useApiClient, { apiRoutes } from "../../hooks/apiClient";
import { formatarData } from "../../components/Formatters/DateTime";
import FileDownloader from "../../components/UI/Button/FileDownloader";
import ButtonImage from "../../components/UI/Button/ButtonImage";
import useDocumentoSpf from "./hooks/useDocumentoSpf";
import { paginatedViewActions } from "../../redux/paginatedView";
import EfisModalJustificativa from "../../components/UI/EfisModal/EfisModalJustificativa";
import funcionalidades from "../../context/AuthContext.funcionalidades";

const documentoSpf = { exibir: () => {} };

const exibirDocumento = (row) => {
  documentoSpf.exibir(row);
};

const acoesCell = (row, setShowAlterarConfirm) => (
  <>
    {!row.documento ? (
      <ButtonImage
        className="btn-lg text-success pa-0 p-0"
        buttonImage={<BsDownload size="16" color="#e37023" />}
        tooltipImage={`Baixar SPF Nº ${row.codigo}`}
        onClick={() => exibirDocumento(row)}
      />
    ) : (
      <FileDownloader binario={row.documento} tooltip={`Baixar SPF Nº ${row.codigo}`} className="ml-2" />
    )}
    {row.stAberturaProcesso !== "C" && funcionalidades.spf.cancelar && (
      <ButtonImage
        className="btn-lg text-danger pa-0 p-0"
        buttonImage={<MdHighlightOff />}
        tooltipImage="Cancelar"
        onClick={() => {
          setShowAlterarConfirm({
            show: true,
            codigo: row.codigo,
            situacao: "C",
            body: `Cancelar SPF número ${row.codigo}`,
          });
        }}
      />
    )}
  </>
);

const columns = [
  {
    name: "Nº SPF",
    cell: (row) => row.codigo,
    maxWidth: "10%",
    sortable: true,
    center: true,
  },
  {
    name: "Data da Inclusão",
    cell: (row) => formatarData(row.dataInclusao),
    maxWidth: "10%",
    center: true,
  },
  {
    name: "Status",
    cell: (row) => {
      switch (row.stAberturaProcesso) {
        case "A":
          return "Aberta";
        case "H":
          return "Homologada";
        case "C":
          return "Cancelada";
        case "R":
          return "Revisão";
        default:
          return "";
      }
    },
    maxWidth: "10%",
    center: true,
  },
  {
    name: "Ciclo",
    cell: (row) => (row.txCodigoOperacional === null ? "" : row.txCodigoOperacional),
    maxWidth: "10%",
  },
  {
    name: "Inscrição Estadual",
    cell: (row) => (row.nuInscEstadual === "" ? " " : <IE value={row.nuInscEstadual} />),
    maxWidth: "12%",
    sortable: true,
    center: true,
  },
  {
    name: "Razão Social",
    cell: (row) => row.nomeFormal,
    maxWidth: "25%",
  },
  {
    name: "Setor de Destino",
    cell: (row) => row.sgOrgao,
    maxWidth: "10%",
  },
  {
    name: "Nº OS",
    cell: (row) => (row.sqOrdemServico === 0 ? "" : row.sqOrdemServico),
    maxWidth: "5%",
    center: true,
  },
  {
    name: "Ações",
    center: true,
    maxWidth: "8%",
    cell: acoesCell,
  },
];

const validationSchema = Yup.object().shape({
  numSpf: Yup.string().default(""),
  sqStatus: Yup.string().default(""),
  sqHomologador: Yup.string().default(""),
  identificador: Yup.string().default(""),
  sqOrgao: Yup.string().default(""),
  sqTipoAcao: Yup.string().default(""),
  sqCiclo: Yup.string().default(""),
  sqOrigem: Yup.string().default(""),
  gerouOs: Yup.string(),
  sqOs: Yup.string().default(""),
  dtInicio: Yup.string().default(""),
  dtFim: Yup.string().default(""),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  initialValues[key] = "";
});

const ConsultaProcedimentoFiscal = ({ history }) => {
  const reduxKey = "procedimentoFiscal";
  const { spfSearchParameters } = useSelector((state) => state.spfSearch);
  const [queryService] = useState(apiRoutes.fiscalizacao.procedimentoFiscal.consultaProcedimentoFiscal);

  const { procedimentoFiscal } = useSelector((r) => r.paginatedView);

  const [showAlterarConfirm, setShowAlterarConfirm] = useState({
    show: false,
    codigo: 0,
    situacao: null,
    body: null,
  });

  const { request: requestCancelar, data: dataCancelar, errors: errorCancelar } = useApiClient();

  const chamaRevisarCancelar = useCallback(
    (values) => {
      setShowAlterarConfirm({ show: false });
      requestCancelar(apiRoutes.fiscalizacao.procedimentoFiscal.cancelaProcedimentoFiscal, values);
    },
    [requestCancelar],
  );

  useEffect(() => {
    if (dataCancelar) {
      window.location.reload();
      // window.location.reload(false);
      toast.success(dataCancelar.result);
    }
    if (errorCancelar) {
      toast.error(errorCancelar.message);
    }
  }, [dataCancelar, errorCancelar]);

  const formik = useFormik({
    initialValues: spfSearchParameters,
    validationSchema,
  });

  const { location } = history;
  const { state } = location;
  const obj = {};
  if (procedimentoFiscal.showAdvancedSearch) {
    obj.status = "T";
  }
  if (state !== undefined) {
    const { status, orgao, pageNumber, pageSize, cpf } = state;
    obj.status = status;
    obj.orgao = orgao;
    obj.pageNumber = pageNumber;
    obj.pageSize = pageSize;
    obj.cpf = cpf;
  }

  const { setDocParams } = useDocumentoSpf();

  documentoSpf.exibir = (row) => {
    setDocParams({ sqAberturaProcesso: row.codigo });
  };

  const dispatch = useDispatch();

  useState(() => {
    // Desestruturando diretamente a partir de history.location.state?.paginatedViewState
    const { advancedSearchParams = {}, showAdvancedSearch = false } = history.location.state?.paginatedViewState || {};

    // Dispatch para salvar os parâmetros de pesquisa
    dispatch(paginatedViewActions.saveParams({ reduxKey, params: advancedSearchParams }));

    // Dispatch para mostrar ou ocultar a busca avançada
    dispatch(paginatedViewActions.setShowAdvancedSearch({ reduxKey, showAdvancedSearch }));

    // Dispatch para definir os parâmetros da barra de título
    dispatch(paginatedViewActions.setTitleBarParams({ reduxKey, titleBarParams: {} }));
  }, [dispatch, history.location.state?.paginatedViewState]);

  if (utils.last(columns).name === "Ações") {
    columns.pop();
  }

  columns.push({
    name: "Ações",
    center: true,
    maxWidth: "4%",
    cell: (row) => acoesCell(row, setShowAlterarConfirm),
  });

  return (
    <>
      {
        <EfisModalJustificativa
          show={showAlterarConfirm.show}
          body={showAlterarConfirm.body}
          codigo={showAlterarConfirm.codigo}
          situacao={showAlterarConfirm.situacao}
          // loadingConfirmationAction={isLoading}
          closeHandler={() => setShowAlterarConfirm({ show: false })}
          confirmationHandler={(codigo, situacao, texto) => {
            chamaRevisarCancelar({
              numero: codigo,
              situacaoAberturaProcesso: situacao,
              textoJustificativa: texto,
            });
          }}
        />
      }
      <SpfPaginatedView
        keyField="codigo"
        descriptionField="descricao"
        defaultSearch
        // defaultSearchPlaceholder="Inscrição Estadual"
        title="SPF"
        queryService={queryService}
        // deleteService={apiRoutes.fiscalizacao.procedimentoFiscal.cancelaProcedimentoFiscal}
        advancedSearchFormik={formik}
        onRowClicked={(item) => {
          window.open(`/app/spf/pendencias/detalhe?numSpf=${item.codigo}`, "_self");
        }}
        columns={columns}
        history={history}
        reduxKey="procedimentoFiscal"
        titleBarParams={{
          situacao: obj.status,
          sqOrgao: obj.orgao,
          pageNumber: obj.pageNumber,
          pageSize: obj.pageSize,
          searchArgument: obj.cpf,
        }}
      >
        <Row>
          <Form.Group as={Col} sm={12} md={2}>
            <Form.Label>Nº SPF</Form.Label>
            <TextInputFormik formik={formik} field="numSpf" />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={2}>
            <Form.Label>Nº OS</Form.Label>
            <TextInputFormik formik={formik} field="sqOs" />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={4}>
            <Form.Label>Situação</Form.Label>
            <SelectInput
              formik={formik}
              field="sqStatus"
              options={[
                { key: "0", value: "", text: "Selecione" },
                { value: "A", text: "ABERTA" },
                { value: "H", text: "HOMOLOGADA" },
                { value: "C", text: "CANCELADA" },
                { value: "R", text: "REVISÃO" },
              ]}
            />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={4}>
            <Form.Label>Homologador</Form.Label>
            <SelectInputCachedFormik formik={formik} field="sqHomologador" tipoItem="homologadores" />
            <br />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={2}>
            <Form.Label>IE/CPF/CNPJ</Form.Label>
            <TextInputFormik formik={formik} field="identificador" />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={2}>
            <Form.Label>Setor de Destino</Form.Label>
            <SelectInputCachedFormik formik={formik} field="sqOrgao" tipoItem="orgaos" />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={4}>
            <Form.Label>Tipo de Ação Fiscal</Form.Label>
            <SelectInputCachedFormik formik={formik} field="sqTipoAcao" tipoItem="tipo-acao-fiscal" />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={4}>
            <Form.Label>Ciclo</Form.Label>
            <SelectInputCachedFormik formik={formik} field="sqCiclo" tipoItem="ciclos" />
            <br />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={2}>
            <Form.Label>Origem</Form.Label>
            <SelectInput
              formik={formik}
              field="sqOrigem"
              options={[
                { key: "0", value: "", text: "Selecione" },
                { value: "I", text: "INDIVIDUAL/LOTE" },
                { value: "C", text: "COBRANÇA" },
                { value: "D", text: "DENÚNCIA" },
                { value: "P", text: "PARTE DE SERVIÇO" },
                { value: "S", text: "PROCESSO SEI" },
              ]}
            />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={2}>
            <Form.Label>OS Gerada</Form.Label>
            <SelectInput
              formik={formik}
              field="gerouOs"
              options={[
                { key: "0", value: "", text: "Selecione" },
                { value: "S", text: "SIM" },
                { value: "N", text: "NÃO" },
              ]}
            />
          </Form.Group>
          <Form.Group className="form-group-periodos" as={Col} sm={12} md={4}>
            <Form.Label>Período de Inclusão</Form.Label>
            <div className="form-inline">
              <DateTimeInputFormik formik={formik} field="dtInicio" />
              <Form.Label className="mx-3"> a </Form.Label>
              <DateTimeInputFormik formik={formik} field="dtFim" />
            </div>
            <br />
          </Form.Group>
        </Row>
      </SpfPaginatedView>
    </>
  );
};

export default ConsultaProcedimentoFiscal;
